import { useEffect, useState } from 'react';
import { pathgen } from 'object-path-generator';
import { ConfirmationModal, Typography, Box, LoadingText } from 'seagull';
import { ProgressBar } from 'seagull/components/ProgressBar/ProgressBar';
import ExternalLinkButton from "../../../components/ExternalLinkButton";
import { useStoreActions } from "../../../contexts";
import styles from "./BulkBillingModal.module.scss";
import { BulkBillingResultsDetails } from "./BulkBillingResultsDetails/BulkBillingResultsDetails";
import { FileDetails } from "./FileDetails/FileDetails";
import { useBulkBillClients } from "./hooks/useBulkBillClients";
import { useUploadBulkBilling } from "./hooks/useUploadBulkBilling";
import { UploadContainer } from "./UploadContainer/UploadContainer";
import { ValidationErrors } from "./ValidationErrors/ValidationErrors";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
export const BulkBillingModalTestIds = pathgen('BulkBillingModalTestIds');
export const BulkBillingModal = _ref => {
  let {
    dismissModal
  } = _ref;
  const {
    wasFileSelected,
    uploadCsv,
    progress: uploadProgress,
    validationErrors,
    isLoading: isLoadingUploadCsv,
    fileDetails,
    reset: resetUploadCsv,
    bulkUploadId
  } = useUploadBulkBilling();
  const {
    billClients,
    progress: billProgress,
    isLoading: isLoadingBillClients,
    wasBillClientsClicked,
    reset: resetBulkBillClients,
    billResults
  } = useBulkBillClients({
    onSuccess: dismissModal,
    onError: dismissModal
  });
  const isLoading = isLoadingUploadCsv || isLoadingBillClients;
  const isBillingInProgress = isLoadingBillClients || !!billProgress;
  const isBillingDoneWithPartialFailure = !!billResults && billResults.type === 'failed';
  const progress = billProgress || uploadProgress;
  const {
    logBI
  } = useStoreActions();
  const [uploadError, setUploadError] = useState();
  const continueBilling = () => {
    billClients(bulkUploadId);
  };
  useEffect(() => {
    if (wasBillClientsClicked && !wasFileSelected) {
      return setUploadError('Uploading a file is required to continue.');
    }
    setUploadError(undefined);
  }, [wasFileSelected, wasBillClientsClicked]);
  const resetModal = () => {
    resetBulkBillClients();
    resetUploadCsv();
    setUploadError(undefined);
  };
  const uploadFile = file => {
    if (file.type !== 'text/csv') {
      logBI(18701, {
        fileType: file.type
      });
      return setUploadError('Invalid file format');
    }
    uploadCsv(file);
  };
  const getPrimaryButton = () => {
    if (isBillingDoneWithPartialFailure) {
      return {
        label: 'Close',
        onClick: dismissModal,
        disabled: false,
        testid: BulkBillingModalTestIds.PrimaryCloseButton()
      };
    }
    if (validationErrors) {
      return {
        label: 'Back',
        onClick: resetModal,
        disabled: false,
        testid: BulkBillingModalTestIds.BackButton()
      };
    }
    if (isBillingInProgress) {
      return {
        label: 'Close',
        onClick: dismissModal,
        disabled: isBillingInProgress,
        testid: BulkBillingModalTestIds.PrimaryCloseButton()
      };
    }
    return {
      label: 'Bill clients',
      onClick: continueBilling,
      disabled: isLoading,
      testid: BulkBillingModalTestIds.BillClientButton()
    };
  };
  const getSecondaryButton = () => {
    if (isBillingDoneWithPartialFailure) {
      return {};
    }
    if (isBillingInProgress) {
      return {};
    }
    return {
      label: 'Close',
      onClick: dismissModal,
      disabled: false,
      testid: BulkBillingModalTestIds.PrimaryCloseButton()
    };
  };
  const primaryButton = getPrimaryButton();
  const secondaryButton = getSecondaryButton();
  const getMainContent = () => {
    if (isBillingDoneWithPartialFailure) {
      return /*#__PURE__*/_jsx(BulkBillingResultsDetails, {
        success: billResults.failed.succeeded,
        failure: billResults.failed.failed,
        bulkUploadId: bulkUploadId
      });
    }
    if (fileDetails) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(FileDetails, {
          name: fileDetails.name,
          lines: fileDetails.lines,
          invoices: fileDetails.invoices,
          invoicesCount: fileDetails.invoicesCount,
          onRemove: resetModal
        }), validationErrors && /*#__PURE__*/_jsx(ValidationErrors, {
          errors: validationErrors
        })]
      });
    }
    return /*#__PURE__*/_jsx(UploadContainer, {
      error: uploadError,
      uploadFile: uploadFile
    });
  };
  return /*#__PURE__*/_jsx(ConfirmationModal, {
    width: "888px",
    title: "Bill clients in bulk",
    "data-testid": BulkBillingModalTestIds.Root(),
    primaryButtonTestid: primaryButton.testid,
    secondaryButtonTestid: secondaryButton.testid,
    textAlignCenter: false,
    primaryButtonDisabled: primaryButton.disabled,
    onPrimaryButtonClick: primaryButton.onClick,
    onCloseButtonClick: dismissModal,
    cancelButtonDisabled: isBillingInProgress,
    primaryButtonLabel: primaryButton.label,
    secondaryButtonLabel: secondaryButton.label,
    onSecondaryButtonClick: secondaryButton.onClick,
    content: /*#__PURE__*/_jsxs(Box, {
      className: styles.content,
      children: [!isBillingDoneWithPartialFailure && /*#__PURE__*/_jsxs(Box, {
        className: styles.title,
        "data-testid": BulkBillingModalTestIds.Header(),
        children: [/*#__PURE__*/_jsx(Typography, {
          children: "Import a list of line items to create and send invoices in bulk."
        }), /*#__PURE__*/_jsx(ExternalLinkButton, {
          href: "http://help.sayanchor.com/en/articles/11378155-billing-clients-in-bulk",
          target: "_blank",
          children: "Learn more about billing in bulk"
        })]
      }), isLoading ? progress ? /*#__PURE__*/_jsxs("div", {
        className: styles.progressBarContainer,
        children: [/*#__PURE__*/_jsxs(Typography, {
          "data-testid": BulkBillingModalTestIds.ProgressBar.Label(),
          children: [isBillingInProgress ? 'Generating invoices' : 'Validating line items', " (", progress.completed, "/", progress.total, ")"]
        }), /*#__PURE__*/_jsx(ProgressBar, {
          "data-testid": BulkBillingModalTestIds.ProgressBar.Bar(),
          completed: progress.completed,
          total: progress.total,
          variant: "steps"
        })]
      }) : /*#__PURE__*/_jsx(LoadingText, {
        "data-testid": BulkBillingModalTestIds.Loader()
      }) : getMainContent()]
    })
  });
};